<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="config-cards-container">
    <PrimeCard
      v-for="item in sortedConfigData"
      :key="item.key"
      class="config-card"
    >
      <template #title>
        <div class="card-title-wrapper">
          <span>
            {{ item.key }}
            <small class="type-badge ml-2">{{
              $t(`config.types.${item.type}`)
            }}</small>
          </span>
          <div class="title-buttons">
            <PrimeButton
              v-if="item.type === 'array' && item.isDefined"
              :icon="
                editorVisibility[item.key]
                  ? 'ri-close-large-line'
                  : 'ri-add-large-line'
              "
              class="p-button-text p-button"
              :aria-label="
                editorVisibility[item.key]
                  ? $t('config.buttons.collapse')
                  : $t('config.buttons.expand')
              "
              @click="toggleEditorVisibility(item.key)"
            />
            <PrimeButton
              v-if="item.isDefined"
              icon="ri-delete-bin-line"
              severity="danger"
              :aria-label="$t('config.buttons.undefine')"
              class="p-button-sm"
              @click="undefineValue(item.key)"
            />
          </div>
        </div>
      </template>
      <template #content>
        <p v-if="descriptions[item.key]?.descriptionKey" class="description">
          {{ $t(descriptions[item.key].descriptionKey) }}
        </p>

        <!-- Show "Define" button if not yet defined -->
        <div v-if="!item.isDefined" class="undefined-container">
          <PrimeButton
            :label="$t('config.buttons.define')"
            icon="ri-add-line"
            class="p-button-outlined"
            @click="defineValue(item.key)"
          />
          <small class="help-text">
            {{ $t("config.notDefined") }}
          </small>
        </div>

        <!-- Show editor if defined -->
        <div
          v-else
          v-show="item.type !== 'array' || editorVisibility[item.key]"
          class="editor-container"
        >
          <PrimeToggleSwitch
            v-if="item.type === 'boolean'"
            :model-value="item.value"
            @update:model-value="updateValue(item.key, $event)"
          />
          <ArrayEditor
            v-else-if="item.type === 'array'"
            :is-metadata="item.key === 'METADATA'"
            :model-value="item.value"
            @update:model-value="updateValue(item.key, $event)"
          />
          <PrimeInputText
            v-else-if="item.type === 'string' || item.type === 'number'"
            :model-value="item.value"
            class="w-full"
            @update:model-value="updateValue(item.key, $event)"
          />
          <pre v-else-if="item.type === 'object'">{{ item.value }}</pre>
        </div>
      </template>
    </PrimeCard>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import PrimeToggleSwitch from "primevue/toggleswitch";
import PrimeCard from "primevue/card";
import PrimeInputText from "primevue/inputtext";
import PrimeButton from "primevue/button";
import ArrayEditor from "@/components/config/ArrayEditor.vue";

const props = defineProps({
  configData: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  descriptions: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:config"]);

const editorVisibility = ref(
  props.configData.reduce((acc, item) => {
    if (item.type === "array") {
      acc[item.key] = false; // Collapsed by default
    }
    return acc;
  }, {}),
);

const toggleEditorVisibility = (key) => {
  editorVisibility.value[key] = !editorVisibility.value[key];
};

const updateValue = (key, value) => {
  const newConfig = props.configData.map((item) => {
    if (item.key === key) {
      return { ...item, value };
    }
    return item;
  });
  emit("update:config", newConfig);
};

const defineValue = (key) => {
  const newConfig = props.configData.map((item) => {
    if (item.key === key) {
      return { ...item, isDefined: true };
    }
    return item;
  });
  emit("update:config", newConfig);
};

const undefineValue = (key) => {
  const newConfig = props.configData.map((item) => {
    if (item.key === key) {
      // Reset to default value from descriptions
      const defaultValue = props.descriptions[key]?.default;
      return { ...item, isDefined: false, value: defaultValue };
    }
    return item;
  });
  emit("update:config", newConfig);
};

const sortedConfigData = computed(() => {
  return [...props.configData].sort((a, b) => a.key.localeCompare(b.key));
});
</script>

<style scoped>
.config-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 1.5rem;
}

.config-card .p-card-title {
  font-size: 1.1rem;
}

.card-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-buttons {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.key-name {
  overflow-wrap: break-word;
  word-break: break-all;
  min-width: 0;
  margin-right: 1rem; /* Add some space between key and badge */
}

.type-badge {
  font-size: 0.8rem;
  font-weight: normal;
  padding: 0.2rem 0.5rem;
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  border-radius: 4px;
}

.description {
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
  margin-bottom: 1rem;
  margin-top: -0.5rem;
}

.editor-container {
  margin-top: 1rem;
}

/* Make the pre tag for objects look nice */
.editor-container pre {
  background-color: var(--p-surface-hover);
  border: 1px solid var(--p-surface-border);
  border-radius: 4px;
  padding: 0.5rem;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 0.8rem;
}

.undefined-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  padding: 1rem;
  background-color: var(--p-content-background);
  border: 1px dashed var(--p-content-border-color);
  border-radius: 6px;
}

.undefined-container .help-text {
  color: var(--p-text-muted-color);
  font-style: italic;
  font-size: 0.85rem;
}
</style>
